export class Globalization {
    private static _actualCulture: string = "cs-CZ";
    private static _numberFormat: INumberFormatInfo;
    private static _timeFormat: ITimeInfo;

    public static get actualCulture(): string {
        return Globalization._actualCulture;
    }

    /**
     * Vrací kulturu ve standardu ISO 639-1 nebo v kombinaci se zkratkou státu, tedy buď en nebo en-US, en-GB, ...
     */
    public static set actualCulture(value: string) {
        Globalization._actualCulture = value;
        Globalization._numberFormat = Globalization.getNumberFormat(value);
        Globalization._timeFormat = Globalization.getTimeFormat(value);
    }

    /**
     * Vrací kulturu ve standardu ISO 639-1, tedy dvoupísmenný formát, např. en, cs, ...
     */
    public static get actualCultureISO639_1(): string {
        return Globalization._actualCulture && Globalization._actualCulture.length > 2 ? Globalization._actualCulture.substr(0, 2) : Globalization._actualCulture;
    }

    public static formatValue(value: any, format?: Intl.NumberFormatOptions | Intl.DateTimeFormatOptions): string {
        return value && value.toLocaleString ? value.toLocaleString(Globalization._actualCulture, format) : value === null || value === undefined ? "" : value + "";
    }

    public static get numberFormat(): INumberFormatInfo {
        if (!Globalization._numberFormat)
            Globalization._numberFormat = Globalization.getNumberFormat(Globalization.actualCulture);
        return Globalization._numberFormat;
    }

    public static get timeFormat(): ITimeInfo {
        if (!Globalization._timeFormat)
            Globalization._timeFormat = Globalization.getTimeFormat(Globalization.actualCulture);
        return Globalization._timeFormat;
    }

    private static _numberFormatCache: Map<string, INumberFormatInfo> = new Map<string, INumberFormatInfo>();
    /**
     * Vrátí number format podle předané kultury
     * @param culture požadovaná kultura - pro aktuální kulturu použijte property NumberFormat
     */
    public static getNumberFormat(culture: string): INumberFormatInfo {
        const lowerCult = culture.toLowerCase();
        if (Globalization._numberFormatCache.has(lowerCult)) return Globalization._numberFormatCache.get(lowerCult) as INumberFormatInfo;

        const numReplace = "#cv#";
        const oneInLoc = (1).toLocaleString(culture, { style: "decimal", maximumFractionDigits: 0 });
        const twoInLoc = (2).toLocaleString(culture, { style: "decimal", maximumFractionDigits: 0 });
        const minusOneInLoc = (-1).toLocaleString(culture, { style: "decimal", maximumFractionDigits: 0 });
        const oneInLocRegex = new RegExp(oneInLoc, "g");

        const minusReplacePosition = minusOneInLoc.replace(oneInLoc, numReplace);

        const curCultureIntGroupCountString = (2111111111).toLocaleString(culture, { useGrouping: true });
        const curCultureIntString = (1111).toLocaleString(culture, { useGrouping: true });
        const curCultureFloatString = (1.1).toLocaleString(culture);

        const orientation: INumberFormatInfoOrienation = curCultureIntGroupCountString.indexOf(twoInLoc) < curCultureIntGroupCountString.indexOf(oneInLoc) ? INumberFormatInfoOrienation.leftToRight : INumberFormatInfoOrienation.rightToLeft;

        const decimalSeparator = curCultureFloatString.replace(oneInLocRegex, "");
        const groupSeparator = curCultureIntString.replace(oneInLocRegex, "");

        let secondaryGroup = 0;
        let primaryGroup = 0;
        for (let i = curCultureIntGroupCountString.length - 1, z = 0; i >= 0; i--) {
            const c = curCultureIntGroupCountString[i];
            if (c === groupSeparator) {
                if (++z >= 2) break;
            } else {
                if (z === 0) secondaryGroup++;
                else primaryGroup++;
            }
        }

        const numbers: string[] = [];
        let useArabicNum = true;

        for (let i = 0; i < 10; i++) {
            const num = (i).toLocaleString(culture, { style: "decimal", maximumFractionDigits: 0 });
            if (num != "" + i) useArabicNum = false;
            numbers.push(num);
        }

        const nf: INumberFormatInfo = {
            decimalSeparator: decimalSeparator,
            groupSeparator: groupSeparator,
            groupSize: primaryGroup,
            secondaryGroupSize: secondaryGroup,
            orientation: orientation,
            numbers: numbers,
            useArabicNumbers: useArabicNum,
            positions: {
                minus: minusReplacePosition,
            },
        };

        Globalization._numberFormatCache.set(lowerCult, nf);

        return nf;
    }

    private static _currencyDataCache: Map<string, ICurrencyInfo> = new Map<string, ICurrencyInfo>();

    /**
     * Vrátí informace o zobrazení mění v určitém jazyce
     * @param culture Nastavení kultůry, ktera se má použít pro formát měny
     * @param currency Měna, která se má v dané kultůře naformátovat
     *
     * @returns Vrací typ ICurrencyInfo, kde jsou informace o znaku, symbolu, lokalizovanem názvu měny a pozici
     * - Informace o pozici se vrati ve formatu obsahujici specialni frazi:
     * #cv# - je treba nahradit danou ciselnou hodnotou
     *
     */
    public static getCurrencyFormatInfo(culture: string, currency: string): ICurrencyInfo {
        const idCD = (culture + "|" + currency).toLowerCase();
        const cultureData = this.getNumberFormat(culture);
        const numReplace = "#cv#";

        if (this._currencyDataCache.has(idCD)) return this._currencyDataCache.get(idCD) as ICurrencyInfo;

        const ci: ICurrencyInfo = {
            minorUnit: 2,
            symbol: "",
            code: "",
            name: "",
            positions: {
                symbol: "",
                code: "",
                name: "",
            },
        };

        const n1 = (1).toLocaleString(culture, { style: "decimal", minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const curSymRegex = new RegExp("\\s*" + n1 + "\\s*");

        const currencySymbolFull = (1).toLocaleString(culture, { style: "currency", currency: currency, currencyDisplay: "symbol", minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const currencyNameFull = (1).toLocaleString(culture, { style: "currency", currency: currency, currencyDisplay: "name", minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const currencyCodeFull = (1).toLocaleString(culture, { style: "currency", currency: currency, currencyDisplay: "code", minimumFractionDigits: 0, maximumFractionDigits: 0 });

        const currencySymbol = currencySymbolFull.replace(curSymRegex, "");
        const currencyName = currencyNameFull.replace(curSymRegex, "");
        const currencyCode = currencyCodeFull.replace(curSymRegex, "");

        const minorUnitFull = (1.222222224).toLocaleString(culture, { style: "currency", currency: currency, currencyDisplay: "code" });
        const minorUnit = minorUnitFull.replace(n1, "").replace(cultureData.decimalSeparator, "").replace(currencyCode, "").replace(/\s+/gi, "");
        ci.minorUnit = minorUnit.length;

        ci.symbol = currencySymbol;
        ci.code = currencyCode;
        ci.name = currencyName;

        ci.positions.symbol = currencySymbolFull.replace(n1, numReplace);
        ci.positions.code = currencyCodeFull.replace(n1, numReplace);
        ci.positions.name = currencyNameFull.replace(n1, numReplace);

        this._currencyDataCache.set(idCD, ci);

        return ci;
    }

    private static _percentDataCache: Map<string, INumberFormatPercentInfo> = new Map<string, INumberFormatPercentInfo>();
    /**
     * Vrátí informace o zobrazení procent v určitém jazyce
     * @param culture Nastavení kultůry, ktera se má použít pro formát procent
     *
     * @returns Vrací typ INumberFormatPercentInfo, kde jsou informace o symbolu a pozici
     * - Informace o pozici se vrati ve formatu obsahujici specialni frazi:
     * #cv# - je treba nahradit danou ciselnou hodnotou
     */
    public static getPercentFormatInfo(culture: string): INumberFormatPercentInfo {
        const lowerCult = culture.toLowerCase();
        if (this._percentDataCache.has(lowerCult)) return this._percentDataCache.get(lowerCult) as INumberFormatPercentInfo;

        const n1 = (0).toLocaleString(culture, { style: "decimal", minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const curSymRegex = new RegExp("\\s*" + n1 + "\\s*");

        const percentSymbolFull = (0).toLocaleString(culture, { style: "percent", minimumFractionDigits: 0, maximumFractionDigits: 0 });
        const percentSymbol = percentSymbolFull.replace(curSymRegex, "");

        const val = {
            symbol: percentSymbol,
            position: percentSymbolFull.replace(n1, "#cv#"),
        };

        this._percentDataCache.set(lowerCult, val);

        return val;
    }

    /**
     * Vrátí regulární výrazy pro kontrolu času na základě vstupního parametru
     * @param timeFormat Časový formát pro ktere se budou vyhodnocovat regulární výrazy
     */
    private static _getTimeRegExp(timeFormat: ITimeInfo): ITimeInfoRegExp {
        const separator = timeFormat.separator === "." ? "\\." : timeFormat.separator;

        if (timeFormat.hour12) {
            //#region Nastaveni regexp
            let validTime12 = /^(0?[1-9]?|1[0-2]?):?([0-5]\d?)?:?([0-5]\d?)?$/i;
            const time12HourSec = new RegExp(
                (timeFormat.amPmOnRight
                    ? "^"
                    : `((${timeFormat.amText}|${timeFormat.pmText})\\s?)?`) +
                `(0?[1-9]|1[0-2])${separator}[0-5][0-9]${separator}[0-5][0-9]` +
                (timeFormat.amPmOnRight
                    ? `(\\s?(${timeFormat.amText}|${timeFormat.pmText}))?$`
                    : "$"), "i");
            const time12HourMin = new RegExp(
                (timeFormat.amPmOnRight
                    ? "^"
                    : `((${timeFormat.amText}|${timeFormat.pmText})\\s?)?`) +
                `(0?[1-9]|1[0-2])${separator}[0-5][0-9]` +
                (timeFormat.amPmOnRight
                    ? `(\\s?(${timeFormat.amText}|${timeFormat.pmText}))?$`
                    : "$"), "i");
            //const TIME12HOUR = new RegExp(
            //    (timeFormat.AmPmOnRight
            //        ? "^"
            //        : `((${timeFormat.AmText}|${timeFormat.PmText})\\s?)?`) +
            //    `(0?[1-9]|1[0-2])${separator}00` +
            //    (timeFormat.AmPmOnRight
            //        ? `(\\s?(${timeFormat.AmText}|${timeFormat.PmText}))?$`
            //        : "$"), "i");
            const time12Hour = new RegExp(
                (timeFormat.amPmOnRight
                    ? "^"
                    : `((${timeFormat.amText}|${timeFormat.pmText})\\s?)?`) +
                "(0?[1-9]|1[0-2])" +
                (timeFormat.amPmOnRight
                    ? `(\\s?(${timeFormat.amText}|${timeFormat.pmText}))?$`
                    : "$"), "i");

            if (timeFormat.useAmPm) {
                let validTime12Str = `(0?[1-9]?|1[0-2]?)${separator}?([0-5]\\d?)?${separator}?([0-5]\\d?)?`;
                let validTime12AmpmText = "(";
                for (let i = 0, len = timeFormat.pmText.length; i < len; i++) {
                    const char = timeFormat.pmText[i];
                    validTime12AmpmText += `${char}${i === 0 ? "" : "?"}`;
                }
                validTime12AmpmText += "|";
                for (let i = 0, len = timeFormat.amText.length; i < len; i++) {
                    const char = timeFormat.amText[i];
                    validTime12AmpmText += `${char}${i === 0 ? "" : "?"}`;
                }
                validTime12AmpmText += ")?";

                if (timeFormat.amPmOnRight) validTime12Str = `^${validTime12Str}\\s?${validTime12AmpmText}$`;
                else validTime12Str = `^${validTime12AmpmText}\\s?${validTime12Str}$`;

                validTime12 = new RegExp(validTime12Str, "i");
            }
            //#endregion Nastaveni regexp
            return {
                timeHour: time12Hour,
                timeMin: time12HourMin,
                timeSec: time12HourSec,
                timeTypeValid: validTime12,
            };
        } else {
            //const TIME24HOUR = new RegExp(`^(0?[0-9]|1[0-9]|2[0-4])${separator}00$`);
            const time24Hour = new RegExp("^(0?[0-9]|1[0-9]|2[0-4])$");
            const time24HourMin = new RegExp(`^(0?[0-9]|1[0-9]|2[0-4])${separator}[0-5][0-9]$`);
            const time24HourSec = new RegExp(`^(0?[0-9]|1[0-9]|2[0-4])${separator}[0-5][0-9]${separator}[0-5][0-9]$`);
            const validTime24 = new RegExp(`^(0?[0-9]?|1[0-9]?|2[0-4]?)${separator}?([0-5][0-9]?)?${separator}?([0-5][0-9]?)?$`);

            return {
                timeHour: time24Hour,
                timeMin: time24HourMin,
                timeSec: time24HourSec,
                timeTypeValid: validTime24,
            };
        }
    }

    /**
     * Vrátí formátovací informace o čase na základě vstupního parametru
     * @param culture kultura, pro kterou bude vrácen její formát - pro aktuální kulturu použijte property TimeFormat
     */
    public static getTimeFormat(culture: string): ITimeInfo {
        //let ampmpattern =   /^(0?[1-9]|1[0-2]):?([0-5]\d)?:?([0-5]\d)?(\s?(AM|PM))$/i;
        const amppmtextPattern = /([0-2]?\d)(\D)([0-5]\d)(\D)([0-5]\d)/i;
        const separatorRegExp = /(?:[0-2]?\d)(\D)(?:[0-5]\d)(?:\D)(?:[0-5]\d)/i;
        let frmtTime: string;
        let frmtTimeAm: string;
        const d = new Date;
        const d_am = new Date;
        d_am.setHours(2, 0, 0, 0);
        d.setHours(14, 0, 0, 0);

        const res: ITimeInfo = {
            hour12: false,
            useAmPm: false,
            pmText: "",
            amText: "",
            separator: ":",
            amPmOnRight: true,
        };

        try {
            frmtTime = d.toLocaleTimeString(culture);
            frmtTimeAm = d_am.toLocaleTimeString(culture);
        } catch (e) {
            frmtTime = d.toLocaleTimeString();
            frmtTimeAm = d_am.toLocaleTimeString();
        }
        const am = frmtTimeAm.replace(amppmtextPattern, "");
        if (am[0] !== " ") res.amPmOnRight = false;

        res.amText = am.trim();
        res.pmText = frmtTime.replace(amppmtextPattern, "").trim();

        if (res.amText.length > 0) res.useAmPm = true;
        const sep = separatorRegExp.exec(frmtTime);
        if (sep && sep.length > 1) {
            res.separator = sep[1];
        }

        const hour12Text = frmtTime.replace(/[\D0]/ig, "");
        if (hour12Text.length === 1) res.hour12 = true;

        //let ampmpattern_text = "(0?[1-9]|1[0-2]):?([0-5]\\d)?:?([0-5]\\d)?";
        //if (res.AmPmOnRight) ampmpattern_text = `^${ampmpattern_text}(\\s?(${res.AmText}|${res.PmText}))$`;
        //else ampmpattern_text = `((${res.AmText}|${res.PmText})\\s?)${ampmpattern_text}$`;

        //const ampmpattern = new RegExp(ampmpattern_text, "i");
        //if (ampmpattern.test(frmt_time)) res.useAmPm = true;

        // Nacteni regularnich vyrazu
        res.regExp = Globalization._getTimeRegExp(res);

        return res;
    }

    /**
     * Vrátí formátovací informace o datu na základě vstupního parametru
     * @param culture kultura, pro kterou bude vrácen její formát
     */
    public static getDateFormat(culture: string): IDateInfo {
        const testDate = new Date(5555, 0, 2); // testovací datum
        let frmtDate: string;
        const ordDate: Array<DateTokenOrder> = [];
        let separatorAfterDay: string = "";
        let separatorAfterMonth: string = "";
        let separatorAfterYear: string = "";

        try {
            frmtDate = testDate.toLocaleDateString(culture);
        } catch (e) {
            frmtDate = testDate.toLocaleDateString();
        }

        let year = 0; // 0 - nic, 1 - zapisuje, 2 - zapsano
        let month = 0;
        let day = 0;
        let yearNumCnt = 0;
        let monthNumCnt = 0;
        let dayNumCnt = 0;

        for (let i = 0, lng = frmtDate.length; i < lng; i++) {
            const num = parseInt(frmtDate[i]);
            if (!isNaN(num)) {
                if (num === 5) {
                    yearNumCnt++;
                    if (month === 1) month = 2;
                    if (day === 1) day = 2;
                    if (year === 0) {
                        year = 1;
                        ordDate.push(DateTokenOrder.year);
                    }
                } else if (num === 1) {
                    monthNumCnt++;
                    if (year === 1) year = 2;
                    if (day === 1) day = 2;
                    if (month === 0) {
                        month = 1;
                        ordDate.push(DateTokenOrder.month);
                    }
                } else if (num === 2) {
                    dayNumCnt++;
                    if (year === 1) year = 2;
                    if (month === 1) month = 2;
                    if (day === 0) {
                        day = 1;
                        ordDate.push(DateTokenOrder.day);
                    }
                } else if (num === 0) {
                    if (year === 1) year = 2;
                    if (day === 1) day = 2;
                    if (month === 1) month = 2;

                    if ((i + 1) < lng) {
                        const n1 = parseInt(frmtDate[i + 1]);
                        if (!isNaN(n1)) {
                            if (n1 === 1) monthNumCnt++;
                            else if (n1 === 2) dayNumCnt++;
                        }
                    }
                }
            } else {
                if (year === 1) separatorAfterYear += frmtDate[i];
                if (day === 1) separatorAfterDay += frmtDate[i];
                if (month === 1) separatorAfterMonth += frmtDate[i];
            }
        }

        const ret: IDateInfo = {
            dateOrder: ordDate,
            year4Digits: yearNumCnt === 4,
            monthBeginZero: monthNumCnt > 1,
            dayBeginZero: dayNumCnt > 1,
            separators: {
                day: separatorAfterDay,
                month: separatorAfterMonth,
                year: separatorAfterYear,
            },
        };

        ret.regExp = Globalization._getDateRegexp(ret);

        return ret;
    }

    private static _getDateRegexp(dateFormat: IDateInfo): { date: RegExp; validate: RegExp } {
        let validDate = "^";
        let fullDate = "^";
        const f_updateSeparator = (sep: string): string => {
            let newSep = "";
            for (let i = 0; i < sep.length; i++) {
                if (sep[i] === ".") newSep += "\\.";
                else newSep += sep[i];
            }

            return newSep;
        };
        for (const ord of dateFormat.dateOrder) {
            if (ord === DateTokenOrder.day) {
                if (dateFormat.dayBeginZero) {
                    fullDate += "(0[0-9]|[1-2][0-9]|3[0-1])";
                } else {
                    fullDate += "([0-9]|[1-2][1-9]|3[0-1])";
                }
                fullDate += f_updateSeparator(dateFormat.separators.day);
            } else if (ord === DateTokenOrder.month) {
                if (dateFormat.monthBeginZero) {
                    fullDate += "(0[0-9]|1[0-2])";
                } else {
                    fullDate += "([0-9]|1[0-2])";
                }
                fullDate += f_updateSeparator(dateFormat.separators.month);
            } else if (ord === DateTokenOrder.year) {
                if (dateFormat.year4Digits) {
                    fullDate += "([0-9]{4})";
                } else {
                    fullDate += "([0-9]{2})";
                }
                fullDate += f_updateSeparator(dateFormat.separators.year);
            }
        }

        fullDate += "$";
        validDate += "$";

        return {
            date: new RegExp(fullDate, "i"),
            validate: new RegExp(validDate, "i"),
        };
    }

    public static getStandardDateTimeOptions(shortFormat: string): Intl.DateTimeFormatOptions | undefined {
        switch (shortFormat) {
            case "d": // Short date pattern
                return {
                    //year: 'numeric', month: '2-digit', day: '2-digit'
                    year: "numeric", month: "numeric", day: "numeric",
                };
            case "D": { // Long date pattern
                const n = Globalization._actualCulture;
                return (n === "en-US" || n.substr(0, 2) === "de")
                    ? { year: "numeric", month: "long", day: "numeric", weekday: "long" }
                    : { year: "numeric", month: "long", day: "numeric" };
            }
            case "f": { // Full date / time pattern(short time)
                const d: any = Globalization.getStandardDateTimeOptions("D");
                const t: any = Globalization.getStandardDateTimeOptions("t");
                for (const att in t) {
                    if (Object.prototype.hasOwnProperty.call(t, att)) {
                        d[att] = t[att];
                    }
                }
                return d;
            }
            case "F": { // Full date / time pattern(long time)
                const d: any = Globalization.getStandardDateTimeOptions("D");
                const t: any = Globalization.getStandardDateTimeOptions("T");
                for (const att in t) {
                    if (Object.prototype.hasOwnProperty.call(t, att)) {
                        d[att] = t[att];
                    }
                }
                return d;
            }
            case "g": // General date / time pattern(short time)
                return { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" };
            //return { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' };
            case "G": // General date / time pattern(long time)
                return { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" };
            //return { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' };
            case "M":
            case "m": // Month / day pattern
                //return { month: 'long', day: '2-digit' };
                return { month: "long", day: "numeric" };
            case "t": // Short time pattern
                return { hour: "numeric", minute: "2-digit" };
            //return { hour: '2-digit', minute: '2-digit' };
            case "T": // Long time pattern
                //return { hour: '2-digit', minute: '2-digit', second: '2-digit' };
                return { hour: "numeric", minute: "2-digit", second: "2-digit" };
            case "U": { // Universal full date / time pattern
                const o = Globalization.getStandardDateTimeOptions("F");
                if (o) o.timeZone = "UTC";
                return o;
            }
            case "Y":
            case "y": // Year month pattern
                return { year: "numeric", month: "long" };
        }

        return undefined;
    }
}

export interface ICultureInfo {
    name: string;
    englishName: string;
    nativeName: string;
    numberFormat: INumberFormatInfo;
    dateTimeFormat: IDateTimeFormatInfo;
}

/**
 * Interface pro číselný formát
 */
export interface INumberFormatInfo {
    /**
     * Oddělovač desetinných míst
     */
    decimalSeparator: string;
    /**
     * Oddělovač tisíců
     */
    groupSeparator: string;
    /**
     * Velikost skupiny pro oddělovač
     */
    groupSize: number;
    /**
     * Nektere jazyky pouzivaji dvojite formatovani, treba en-IN pouziva cislo ve formatu 1,00,00,000
     */
    secondaryGroupSize: number;
    /**
     * Orientace psaní čísel
     */
    orientation: INumberFormatInfoOrienation;
    /**
     * Mapa čísel pro danou culture, pokud nejsou psány arabskymi pismeny.
     * Mapování odpovídá [0] => 0, [1] => 1 ... [9] => 9
     */
    numbers: string[];
    /**
     * Indikace použití standardních arabských čísel
     */
    useArabicNumbers: boolean;
    /**
     * Poziční stringy, obsahujici nahrazujici sekvenci #cv#
     * - minus - pozicni string pro minus (- #cv#)
     */
    positions: {
        minus: string;
    };
}

export enum INumberFormatInfoOrienation {
    leftToRight,
    rightToLeft,
}

/**
 * Interface pro zápis měny
 */
export interface ICurrencyInfo {
    minorUnit: number;
    /**
     * Symbol měny (€)
     */
    symbol: string;
    /**
     * Kód měny (EUR)
     */
    code: string;
    /**
     * Lokalizovaný název měny (Euro)
     */
    name: string;
    /**
     * Poziční stringy, obsahujici nahrazujici sekvenci #cv#
     * - symbol - pozicni string pro minus (#cv# €)
     * - code - pozicni string pro minus (#cv# EUR)
     */
    positions: {
        symbol: string;
        code: string;
        name: string;
    };
}

export interface INumberFormatPercentInfo {
    symbol: string;
    position: string;
}

export interface IDateTimeFormatInfo {
    monthNames: string[];
    dayNames: string[];
    abbreviatedMonthNames: string[];
    abbreviatedDayNames: string[];
    pmDesignator: string;
    amDesignator: string;
    formatPatterns: IFormatPatterns;
}

export interface IFormatPatterns {
    shortDate: string;
    longDate: string;
    shortTime: string;
    longTime: string;
    fullDateTime: string;
    sortableDateTime: string;
    universalSortableDateTime: string;
    rfc1123: string;
    monthDay: string;
    yearMonth: string;
}

export interface ITimeInfoRegExp {
    timeHour: RegExp;
    timeMin: RegExp;
    timeSec: RegExp;
    timeTypeValid: RegExp;
}

export interface ITimeInfo {
    hour12: boolean;
    useAmPm: boolean;
    amText: string;
    pmText: string;
    separator: string;
    amPmOnRight: boolean; // pozice am/pm v textu
    regExp?: ITimeInfoRegExp;
}

export interface IDateInfo {
    dateOrder: Array<DateTokenOrder>;
    year4Digits: boolean;
    monthBeginZero: boolean;
    dayBeginZero: boolean;
    separators: {
        day: string;
        month: string;
        year: string;
    };
    regExp?: IDateInfoRegExp;
}

export interface IDateInfoRegExp {
    date: RegExp;
    validate: RegExp;
}

export enum DateTokenOrder {
    day,
    month,
    year,
}
